import { type FC, type ReactNode, useEffect, useMemo } from "react";
import { Item } from "react-stately";

import { useDataAttributes } from "../../../hooks.ts";
import { Icon } from "../../Icon/Icon.tsx";
import { VStack } from "../../Stack/VStack.tsx";
import { useResizableTabPanelsContext } from "../hooks/use-panel-group-context.ts";
import style from "../PanelGroup.module.css";
import { extractTabsFromChildren } from "../utils/extract-tabs-from-children.ts";
import { PanelTabs } from "./PanelTabs.tsx";

export interface FixedPanelProps {
  children: ReactNode;
  showTabsLabel?: boolean;
}

// FixedPanel component - detects if it has tab children
export const FixedPanel: FC<FixedPanelProps> = (props) => {
  const context = useResizableTabPanelsContext();

  const { children, showTabsLabel } = props;

  const flexStyle = useMemo(() => {
    return {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: context.isOpen ? (context.currentSize ?? "50%") : context.closedSize,
    };
  }, [context.isOpen, context.currentSize, context.closedSize]);

  // Check if we have FixedPanelTab children
  const tabs = extractTabsFromChildren(children);
  const hasTabChildren = tabs.length > 0;

  const dataAttributes = useDataAttributes(props);

  // Set initial active tab if none is set
  useEffect(() => {
    if (hasTabChildren && !context.activeTabId && tabs.length > 0) {
      context.setActiveTabId(tabs[0].id);
    }
  }, [hasTabChildren, context, tabs]);

  return (
    <div
      className={style.pane}
      style={flexStyle}
      data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}fixed-panel`}
    >
      {hasTabChildren ? (
        <VStack spacing={1} className={style.icontabs}>
          <PanelTabs
            items={tabs}
            onSelectionChange={context.setActiveTabId}
            selectedKey={context.activeTabId}
            tabsActions={context.tabsActions}
            showLabel={showTabsLabel}
          >
            {(tab) => (
              <Item
                key={tab.id}
                title={
                  showTabsLabel ? (
                    <VStack
                      spacing={0}
                      style={{
                        alignItems: "center",
                        fontSize: 12,
                        lineHeight: "12px",
                        fontWeight: 450,
                      }}
                    >
                      <Icon icon={tab.icon} aria-hidden="true" className={style.icon} />
                      {tab.title}
                    </VStack>
                  ) : (
                    <Icon icon={tab.icon} className={style.icon} />
                  )
                }
                textValue={tab.title}
              >
                {/* This is mandatory, but empty. Move all rendering into title */}
                {tab.children}
              </Item>
            )}
          </PanelTabs>
        </VStack>
      ) : (
        children
      )}
    </div>
  );
};
